import React, { Fragment, useContext, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

// Selectors

import {
    selectIfOnlyUpdatedFieldsShown,
    selectIfFinalReviewPageActive,
    selectIfExtraFinalSummaryShown,
    selectIfPreviousDataLoaded
} from '../../../../../reducers/appReducer';

// Context

import { Context } from '../../../../../contexts/ssapSummaryContext';

// Hooks

import { useSummaryItemDiffRegister } from '../../../../../hooks/useSummaryItemDiffRegister';

// Utils

import { extractPreviousData } from '../../../../../utils/miscellaneous/extractPreviousData';
import { localStorageHyperDashboardTools } from '../../../../../utils/tools/localStorageHyperDashboardTools';

const SummaryItem = props => {
    const NOT_PROVIDED = 'NOT_PROVIDED';

    let {
        additionalElement,
        additionalElementClassName,
        category,
        currentData,
        ignoreIntl,
        ignoreTextCode,
        isDiffSwitchingIgnored = false,
        nullValue,
        previousData,
        renderAs,
        renderLabel = value => value,
        replacements,
        textCode,
        summaryItemId = '',
        value,
        desc = NOT_PROVIDED,
        isStrikeThroughValue = false,
        booleanAsAgree = false
    } = props;

    const intlApi = useIntl();

    const areOnlyUpdatedFieldsShown = useSelector(selectIfOnlyUpdatedFieldsShown);
    const isExtraFinalSummaryShown = useSelector(selectIfExtraFinalSummaryShown);
    const isFinalSummaryPage = useSelector(selectIfFinalReviewPageActive);
    const isPreviousSsapApplicationLoaded = useSelector(selectIfPreviousDataLoaded);

    const summaryContext = useContext(Context);

    const {
        indexMapper = {
            currentValues: { },
            previousValues: { }
        },
        registeredSummaryItemsWithDiffs,
        summaryId,
        summaryItemsComparisonEnabled,
    } = summaryContext;

    const shouldDefaultPreviousData = !isFinalSummaryPage || (isFinalSummaryPage && !isExtraFinalSummaryShown);

    const {
        currentValue,
        previousValue
    } = extractPreviousData(currentData, previousData, desc, value, shouldDefaultPreviousData, indexMapper);

    value = currentValue;

    const intlBoolean = value => {
        if(booleanAsAgree) {
            return intlApi.formatMessage({ id: value ? 'common.agree' : 'common.disagree' });
        }
        return intlApi.formatMessage({ id: value ? 'common.yes' : 'common.no' });
    };

    const isMultiColumnItem = Array.isArray(value);

    const getIntlValue = (id, replacements = {}, metaData) => {
        switch(true) {
            case nullValue && id === null:
                return intlApi.formatMessage({ id: nullValue }, replacements);
            case typeof id === 'boolean' || id === null:
                return intlBoolean(id);
            default:
                if(id === undefined) console.log('getIntlValue failure: ', { ...metaData, props });
                return intlApi.formatMessage({ id }, replacements);
        }
    };

    const diffs = useMemo(
        () => {
            let genericInfo = {
                textCode,
                category
            };

            if(!isPreviousSsapApplicationLoaded) {
                return {
                    ...genericInfo,
                    status: false,
                    indexes: []
                };
            }

            // Use case: summaryId absence indicates that SummaryItem should not render any diffs with previous value
            if(!summaryId) {
                return {
                    ...genericInfo,
                    status: false,
                    indexes: []
                };
            }

            // Since previousValue is not provided that means SummaryItem should be back compatible with prior behavior

            if (previousValue === NOT_PROVIDED) {
                return {
                    ...genericInfo,
                    status: false,
                    indexes: []
                };
            }

            // Here we got multi-column SummaryItem.
            // If diffs are detected then status is set to true, and their indexes are populated into the indexes array
            // If diffs are not detected then status is set to false, and the indexes array stays empty

            if (Array.isArray(currentValue)) {
                let diffs = {
                    ...genericInfo,
                    status: false,
                    indexes: []
                };

                currentValue.forEach((v, idx) => {

                    const printedCurrentValue = ignoreIntl[idx]
                        ? v
                        : getIntlValue(v, {}, { id: 1, dataType: 'current', caller: 'diffs: array' });

                    const printedPreviousValue = ignoreIntl[idx]
                        ? previousValue[idx]
                        : getIntlValue(previousValue[idx], {}, { id: 2, dataType: 'previous', caller: 'diffs: array' });

                    if (printedCurrentValue !== printedPreviousValue) {
                        diffs = {
                            ...genericInfo,
                            status: true,
                            indexes: [...diffs.indexes, idx]
                        };
                    }
                });
                return diffs;
            } else {

                // Here we got single-column SummaryItem.
                // If diff is detected then status is set to true
                // If diff is not detected then status is set to false

                const printedCurrentValue = ignoreIntl
                    ? currentValue
                    : getIntlValue(currentValue, {}, { id: 3, dataType: 'current', caller: 'diffs: not an array' });

                const printedPreviousValue = ignoreIntl
                    ? previousValue
                    : getIntlValue(previousValue, {}, { id: 4, dataType: 'previous', caller: 'diffs: not an array' });

                return {
                    ...genericInfo,
                    status: printedCurrentValue !== printedPreviousValue,
                    indexes: null
                };
            }
        },
        [ currentValue, previousValue ]
    );

    const defaultClasses = {
        itemLabelClasses: 'summary__label tablet:grid-col-6',
        itemValueClasses: 'summary__value tablet:grid-col-6'
    };

    const classNamesConfig = {
        addressItemClasses: {
            itemLabelClasses: 'address__label tablet:grid-col-6',
            itemValueClasses: 'address__value tablet:grid-col-6'
        },
        addressItemClassesWithSidePaddings: {
            itemLabelClasses: 'address__label padding-l-5-px padding-r-5-px tablet:grid-col-6',
            itemValueClasses: 'address__value padding-l-5-px padding-r-5-px tablet:grid-col-6'
        },
        gridForSixColsCustomWidth: {
            gridLabelClasses: 'grid-col-3',
            gridValueClasses: 'custom-width'
        },
        gridForFourClasses: {
            gridLabelClasses: 'grid-col-4',
            gridValueClasses: 'grid-col-2'
        },
        gridForFourAllEqualClasses: {
            gridLabelClasses: 'grid-col-3',
            gridValueClasses: 'grid-col-3'
        },
        summaryItemZeroPaddingClasses: {
            itemLabelClasses: 'summary__label padding-0 tablet:grid-col-6',
            itemValueClasses: 'summary__value padding-0 tablet:grid-col-6'
        },
        summaryItemBorderedClasses: {
            itemLabelClasses: 'summary__label tablet:grid-col-12 bordered-b',
            itemValueClasses: 'summary__value'
        },
        summaryItemMinPaddingClasses: {
            itemLabelClasses: 'summary__label tablet:grid-col-5 summary-min-padding',
            itemValueClasses: 'summary__value tablet:grid-col-6 summary-min-padding'
        },
    };

    const getClassNames = () => {
        return {
            ...defaultClasses,
            ...Object.keys(classNamesConfig).reduce((classes, prop) => {
                return props[prop] ?
                    {
                        ...classes,
                        ...classNamesConfig[prop]
                    } :
                    classes;
            }, {})
        };
    };

    const isConsolingOn = localStorageHyperDashboardTools.getValueByPropName('isReactIntlWarningsShown', false);

    const wrapStringWithLessGreater = str => `\u003C${str}\u003E`;

    useSummaryItemDiffRegister({
        diffs,
        summaryContext,
        summaryItemId,
        values: {
            category,
            value,
            previousValue,
            textCode
        }
    });

    if(value === undefined || (textCode === undefined && category === undefined)) {
        isConsolingOn && console.log(`${wrapStringWithLessGreater('value')} or both (${wrapStringWithLessGreater('textCode')} and ${wrapStringWithLessGreater('category')}) hasn't been provided to ${wrapStringWithLessGreater('SummaryItem/')} Component. ${wrapStringWithLessGreater('SummaryItem/')} Component has been skipped in render`);
        return null;
    }

    const internationalizeValue = (value, tag, ignored, attributes, isPreviousValue) => {

        const isValueArray = Array.isArray(value);
        const dataType = isPreviousValue ? 'previous' : 'current';

        const refineAttributes = (attributes, prevValue, currentValue, idx = -1) => {

            const strikethroughTextConditions = [
                isPreviousValue,
                summaryItemsComparisonEnabled,
                previousValue !== NOT_PROVIDED,
                ~idx ? diffs.indexes.includes(idx) : prevValue === currentValue
            ];

            const isStrikethroughText = isStrikeThroughValue
                ? true
                : strikethroughTextConditions.every(condition => condition === true);

            return {
                ...attributes,
                className: `${ attributes.className } ${ isStrikethroughText ? 'text-strike-review' : '' }`
            };
        };

        if(isValueArray) {

            return value.map((v, idx) => {

                const isIgnored = Array.isArray(ignored) && ignored.includes(idx);

                const intlized = isIgnored
                    ? v
                    : getIntlValue(
                        v,
                        replacements && replacements[idx] ? replacements[idx] : {},
                        { dataType, caller: 'internationalizeValue: array' }
                    );

                let refinedAttributes = refineAttributes(attributes, previousValue[idx], v, idx);

                if(intlized === v) {
                    isConsolingOn && !isIgnored && console.log(`Value ${wrapStringWithLessGreater(v)} cannot be internationalized`);
                    return React.createElement(tag, { ...refinedAttributes, key: idx }, v);
                } else {
                    return React.createElement(tag, { ...refinedAttributes, key: idx }, intlized);
                }
            });
        } else {
            const intlized = ignored
                ? value
                : getIntlValue(
                    value,
                    replacements,
                    { dataType, caller: 'internationalizeValue: not an array' }
                );

            let refinedAttributes = refineAttributes(attributes, previousValue, value);

            return React.createElement(tag, refinedAttributes, intlized );
        }
    };

    const {
        gridLabelClasses,
        gridValueClasses,
        itemLabelClasses,
        itemValueClasses
    } = getClassNames();

    const getLabelPlaceholder = (tag, classNameOverrides = '') => React
        .createElement(
            tag,
            { className: classNameOverrides ? classNameOverrides : itemLabelClasses }
        );

    const getSingleLabel = isPreviousValue => {
        let isLabelShown;

        switch(true) {
            case isPreviousValue:
            case previousValue === NOT_PROVIDED:
            case !diffs.status:
            case diffs.status && !summaryItemsComparisonEnabled:
                isLabelShown = true;
                break;
            default:
                isLabelShown = false;
        }

        const tag = renderAs ? renderAs : 'div';

        const value = renderLabel(
            textCode ? textCode : category,
            {
                registeredSummaryItemsWithDiffs,
                summaryItemId
            },
            areOnlyUpdatedFieldsShown
        );

        const isIgnored = textCode ? ignoreTextCode : false;

        return isLabelShown
            ? (
                internationalizeValue(
                    value,
                    tag,
                    isIgnored,
                    { className: itemLabelClasses }
                )
            )
            : getLabelPlaceholder(tag);
    };

    const getSingleValue = (value, isPreviousValue) => {
        return internationalizeValue(
            value,
            renderAs ? renderAs : 'div',
            ignoreIntl,
            { className: itemValueClasses },
            isPreviousValue
        );
    };
    const getMultipleLabel = isPreviousValue => {
        let isLabelShown;

        switch(true) {
            case isPreviousValue:
            case previousValue === NOT_PROVIDED:
            case !diffs.status:
            case diffs.status && !summaryItemsComparisonEnabled:
                isLabelShown = true;
                break;
            default:
                isLabelShown = false;
        }

        const tag = renderAs ? renderAs : 'td';

        return isLabelShown
            ? internationalizeValue(
                textCode ? textCode : category,
                tag,
                textCode ? ignoreTextCode : ignoreIntl,
                { className: gridLabelClasses }
            )
            : getLabelPlaceholder(
                tag,
                gridLabelClasses
            );
    };

    const getMultipleValue = (value, isPreviousValue) => {
        return internationalizeValue(
            value,
            renderAs ? renderAs : 'td',
            ignoreIntl,
            { className: gridValueClasses },
            isPreviousValue
        );
    };

    const getContent = (value, isPreviousValue) => {

        return !isMultiColumnItem
            ? (
                <div className = 'grid-row' >
                    { getSingleLabel(isPreviousValue) }

                    { getSingleValue(value, isPreviousValue) }
                </div>
            )
            : (
                <Fragment>
                    <tr className = 'grid-row' >
                        { getMultipleLabel(isPreviousValue) }

                        { getMultipleValue(value, isPreviousValue) }

                        {
                            additionalElement &&

                            <td className={additionalElementClassName}>
                                { additionalElement }
                            </td>
                        }
                    </tr>
                </Fragment>
            );
    };

    if(areOnlyUpdatedFieldsShown && summaryId && !diffs.status && !isDiffSwitchingIgnored) {
        return null;
    }

    const displayUpdatedRow = diffs.status ? 'grid-col-12 bg-updated_fields' : 'grid-col-12';

    return (
        <Fragment>
            <div className = { displayUpdatedRow } >
                {
                    isPreviousSsapApplicationLoaded
                    && summaryItemsComparisonEnabled
                    && diffs.status
                    && getContent(previousValue, true)
                }
                { getContent(value, false) }
            </div>
        </Fragment>
    );
};

const mapStateToProps = (state) => ({
    currentData: state.ssap.data,
    previousData: state.ssap.previousData,
    messages: state.common.messages
});

export default connect(mapStateToProps)(SummaryItem);
